import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, InputNumber, notification, Select, Spin, Table, Modal } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useAuth } from "services/Auth";
import PageLayoutWrapper from "components/PageLayoutWrapper";
import { createCountedStocksPayloadInStore, offlineHandler, updateCountedQuantity } from "utils/offlineCache";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { isNil, isNilEmptyOrEmptyObject } from "@salespark/toolkit";
import useSelectedService from "hooks/useSelectedService";
import { locationTracker } from "utils/location/geolocation";
import { generateActionReference } from "utils/location/record";
import { LOCATION_ACTIONS } from "constants/location";

function Stock() {
  const { auth } = useAuth();
  const apiClient = useMemo(() => offlineHandler(auth.data), [auth.data]);
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [filter] = useState({});
  const [form] = Form.useForm();
  const [stonksData, setStonksData] = useState([]);
  const [waresData, setWaresData] = useState([]);
  const { selectedService } = useSelectedService();

  useEffect(() => {
    if (stonksData.length > 0) {
      form.setFieldsValue({
        stocks: stonksData.map((s) => ({
          _id: s._id,
          wares: s.wares?._id,
          counted_quantity: s.counted_quantity || 0,
          isNew: false,
        })),
      });
    }
  }, [stonksData, form]);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      let filters = {
        ...filter,
      };
      if (selectedService) {
        filters.equipment = { $in: selectedService.equipment.map((e) => e._id) };
      }
      if (!selectedService?.equipment?.length) {
        await apiClient.post("/error-capture", {
          error: selectedService,
          action: "/Stocks/loadData/getMany /service.equipment filter",
          timestamp: new Date().toISOString(),
        });
        notification.error({
          title: "Erro",
          description: "Este serviço não tem um equipamento selecionado",
        });
        return;
      }
      const equipments = selectedService.equipment.map((e) => e._id);

      // Get Offline para ter as counted_quantities submetidas, tal como no stock dos consumos e vendas
      const stonksResponse = await apiClient.getOffline("/goods/stocks");
      const stonksResponse2 = await apiClient.getOffline("countedStocks");

      console.log("stonksResponse", stonksResponse);
      console.log("stonksResponse2", stonksResponse2);
      if (stonksResponse.status === false) {
        notification.error({
          title: "Erro",
          description: "Erro ao carregar as existências",
        });
        await apiClient.post("/error-capture", {
          error: stonksResponse.data,
          action: "/Stocks/loadData/getMany /goods/stocks",
          timestamp: new Date().toISOString(),
        });
        return;
      }
      stonksResponse.data.sort((a, b) => a.equipment?.name.localeCompare(b.equipment?.name));
      const stocks = stonksResponse.data.filter((s) => s.wares?.type === "goods" && equipments.includes(s.equipment?._id));
      stocks.sort((a, b) => a.wares?.order - b.wares?.order);
      console.log("stocks", stocks);
      setStonksData(stocks);

      const waresResponse = await apiClient.getMany("/goods/wares");
      if (waresResponse.status === false) {
        notification.error({
          title: "Erro",
          description: "Erro ao carregar as existências",
        });
        await apiClient.post("/error-capture", {
          error: waresResponse,
          action: "/Stocks/loadData/getMany wares",
        });
        return;
      }
      const wares = waresResponse.data.filter((w) => w.type === "goods").sort((a, b) => a.order - b.order);

      if (wares.length === 0) {
        notification.error({
          title: "Erro",
          description: "Erro ao carregar os artigos, por favor recarrega a página e tenta novamente",
        });
        await apiClient.post("/error-capture", {
          error: { waresResponse: waresResponse.data },
          action: "/Stocks/loadData/filtered wares",
          timestamp: new Date().toISOString(),
        });
      }
      setWaresData(wares);

      // Error handling
    } catch (error) {
      notification.error({
        title: "Erro",
        description: "Erro ao carregar as existências",
      });
      await apiClient.post("/error-capture", {
        error: error,
        action: "/Stock/loadData/catch",
        timestamp: new Date().toISOString(),
      });

      // This is Sparta Mode
    } finally {
      setIsLoading(false);
    }
  }, [apiClient, filter, selectedService]);

  useEffect(() => {
    if (!selectedService) return;
    loadData();
  }, [selectedService, loadData]);

  const onFinish = async () => {
    try {
      if (isNil(form)) return;

      setIsLoading(true);
      const values = form.getFieldValue();

      if (isNilEmptyOrEmptyObject(values) || isNilEmptyOrEmptyObject(values.stocks)) {
        notification.info({
          title: "Info",
          description: "Sem dados de contagens",
        });
        return;
      }
      if (selectedService?.equipment?.length === 0) {
        Modal.error({
          title: "Erro",
          content: "Não existe nenhum equipamento associado a este serviço",
        });
        return;
      }

      const equipment = {
        name: selectedService.equipment[0].name,
        _id: selectedService.equipment[0]._id,
      };
      for (const element of values.stocks) {
        const ware = waresData.find((w) => w._id === element.wares);
        if (isNil(ware)) {
          notification.error({
            title: "Erro",
            description: "Erro ao atualizar as contagens",
          });
          await apiClient.post("/error-capture", {
            error: { wareData: waresData, element: element },
            action: "/Stock/onFinish/ forloop wareData",
            timestamp: new Date().toISOString(),
          });
          return;
        }
        element.wares = {
          description: ware.description,
          type: ware.type,
          _id: ware._id,
        };
      }

      const payload = {
        ...values,
        equipment,
      };
      const response = await updateCountedQuantity(payload);

      if (response.status === false) {
        notification.error({
          title: "Erro",
          description: "Erro ao atualizar as existências",
        });
        await apiClient.post("/error-capture", {
          error: response.data,
          action: "/Stock/onFinish/ updateCountedQuantity",
          timestamp: new Date().toISOString(),
        });
        return;
      }
      if (response.data.length > 0) {
        const key = `${auth?.data?._id || "nouser"}_${selectedService.service_id_raw}_${selectedService.equipment[0]._id}`;
        const countedStocksPayload = {
          ...values,
          equipment: selectedService.equipment[0]._id,
          service_id_raw: selectedService.service_id_raw,
          user_id: auth?.data?._id || null,
          date: dayjs().toDate(),
          product: selectedService.product._id,
          id: key,
        };

        const countingResponse = await createCountedStocksPayloadInStore(countedStocksPayload);
        if (countingResponse.status === false) {
          console.log("countingResponse", countingResponse);
          notification.error({
            title: "Erro",
            description: "Erro ao atualizar as existências",
          });
          await apiClient.post("/error-capture", {
            error: countingResponse.data,
            action: "/Stock/onFinish/ createCountedStocksPayloadInStore",
            timestamp: new Date().toISOString(),
          });
          return;
        }
      }

      // Capture the crate-count location (best-effort; never blocks the action).
      locationTracker.captureActionLocation({
        service_id_raw: selectedService?.service_id_raw || null,
        equipment_id: selectedService?.equipment?.[0]?._id || null,
        action: LOCATION_ACTIONS.CRATE_COUNT,
        action_reference: generateActionReference(),
      });

      // Send info capture to the server for debugging purposes
      await apiClient.post("/info-capture", {
        extraData: {
          location: "src/views/app-views/goods/stocks/index.js/onFinish",
          timestamp: new Date().toISOString(),
        },
        info: {
          location: "src/views/app-views/goods/stocks/index.js/onFinish",
          timestamp: new Date().toISOString(),
          service_id: selectedService?.service_id_raw || null,
          equipment: selectedService?.equipment?.[0]?._id || null,
          payload: payload,
        },
        subject: "Boats: Mobile Stock Counting",
        message: `Boats: Cooler counting saved to local store (${auth?.data?.name || "Unknown User"})`,
      });

      window.scrollTo({ top: 0, behavior: "smooth" });
      history.push("/home");

      // Error handling
    } catch (error) {
      console.log("error", error);
      notification.error({
        title: "Erro",
        description: "Erro ao atualizar as existências",
      });
      await apiClient.post("/error-capture", {
        error: error,
        action: "/Stock/onFinish/ catch",
        timestamp: new Date().toISOString(),
      });

      // This is Sparta Mode
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageLayoutWrapper title="Contagem de Arcas">
      <Spin spinning={isLoading} fullscreen />
      <Form form={form} id="stock-form" layout="vertical" onFinish={onFinish} className="compact-form">
        <Form.List name="stocks">
          {(fields, { add, remove }) => (
            <>
              <Table
                size="small"
                pagination={false}
                dataSource={fields}
                rowKey="key"
                className="compact-table"
                tableLayout="fixed"
                columns={[
                  {
                    title: "Artigo",
                    render: (_, field) => (
                      <Form.Item shouldUpdate noStyle>
                        {() => {
                          const stocks = form.getFieldValue("stocks") || [];
                          const stock = stocks?.[field.name];

                          const usedWareIds = stocks.map((s, index) => (index !== field.name ? s?.wares : null)).filter(Boolean);

                          if (stock?.isNew) {
                            return (
                              <Form.Item name={[field.name, "wares"]} rules={[{ required: true, message: "Selecione um artigo" }]} style={{ marginBottom: 0 }}>
                                <Select
                                  size="large"
                                  placeholder="Selecione artigo"
                                  style={{ minWidth: 200 }}
                                  options={waresData
                                    .filter((w) => !usedWareIds.includes(w._id))
                                    .map((ware) => ({
                                      value: ware._id,
                                      label: ware.description,
                                    }))}
                                />
                              </Form.Item>
                            );
                          }
                          const wareObj = waresData.find((w) => w._id === stock?.wares);
                          return (
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span>{wareObj?.description}</span>
                              <span> {wareObj?.movement_unit || ""}</span>
                            </div>
                          );
                        }}
                      </Form.Item>
                    ),
                  },

                  {
                    title: "Quantidade Contada",
                    render: (_, field) => (
                      <Form.Item
                        name={[field.name, "counted_quantity"]}
                        rules={[{ required: true, message: "Insire a quantidade" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber min={0} size="large" style={{ width: 120 }} controls />
                      </Form.Item>
                    ),
                  },

                  {
                    title: "",
                    width: 80,
                    render: (_, field) => (
                      <Form.Item shouldUpdate noStyle>
                        {() => {
                          const stocks = form.getFieldValue("stocks") || [];
                          const stock = stocks?.[field.name];

                          if (!stock?.isNew) return null;

                          return (
                            <Button danger size="middle" onClick={() => remove(field.name)}>
                              X
                            </Button>
                          );
                        }}
                      </Form.Item>
                    ),
                  },
                ]}
              />

              <Button
                type="dashed"
                block
                icon={<PlusOutlined />}
                onClick={() =>
                  add({
                    wares: null,
                    counted_quantity: null,
                    isNew: true,
                  })
                }
              >
                Adicionar Artigo
              </Button>
            </>
          )}
        </Form.List>
        <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
          <Button type="primary" htmlType="submit" size="large" form="stock-form">
            Confirmar Contagem
          </Button>
        </div>
      </Form>
    </PageLayoutWrapper>
  );
}

export default Stock;
